import * as cheerio from 'cheerio';
import { isTag, type AnyNode } from 'domhandler';

type CheerioSelection = cheerio.Cheerio<AnyNode>;

const SANITIZE_ROOT_ID = '__contact_us_sanitize_root__';

const ALLOWED_TAGS = new Set([
  'p',
  'br',
  'div',
  'span',
  'a',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'ul',
  'ol',
  'li',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
  'strong',
  'em',
  'b',
  'i',
  'u',
]);

/** Removed completely — content is not kept. */
const BLOCKED_TAGS = new Set([
  'script',
  'iframe',
  'form',
  'input',
  'object',
  'embed',
  'link',
  'meta',
  'style',
]);

const SAFE_LINK_PROTOCOLS = new Set(['http:', 'https:', 'mailto:', 'tel:']);

/** Strip XSS vectors from inline or embedded CSS while allowing normal styling. */
const DANGEROUS_CSS_PATTERNS = [
  /javascript\s*:/gi,
  /expression\s*\(/gi,
  /behavior\s*:/gi,
  /-moz-binding/gi,
  /url\s*\(\s*['"]?\s*javascript/gi,
];

const SAFE_CLASS_TOKEN = /^[a-zA-Z][\w-]*$/;

const GLOBAL_ATTRS = new Set(['class', 'style']);

/** No tag-specific attributes beyond globals (strips class, id, on*, href, src, etc.). */
const NO_ATTRS = new Set<string>();

const ANCHOR_ATTRS = new Set(['href', 'rel']);

const TABLE_CELL_ATTRS = new Set(['colspan', 'rowspan']);

const ALLOWED_ATTRS_BY_TAG: Record<string, Set<string>> = {
  a: ANCHOR_ATTRS,
  th: TABLE_CELL_ATTRS,
  td: TABLE_CELL_ATTRS,
};

function getTagName(node: AnyNode): string {
  return isTag(node) ? node.name.toLowerCase() : '';
}

export function sanitizeCssText(css: string): string {
  let result = css;
  for (const pattern of DANGEROUS_CSS_PATTERNS) {
    result = result.replace(pattern, '');
  }
  return result.trim();
}

export function sanitizeClassAttribute(
  value: string | undefined
): string | undefined {
  if (!value?.trim()) {
    return undefined;
  }
  const tokens = value
    .trim()
    .split(/\s+/)
    .filter((token) => SAFE_CLASS_TOKEN.test(token));
  return tokens.length > 0 ? tokens.join(' ') : undefined;
}

function getAllowedAttributes(tagName: string): Set<string> {
  const tagSpecific = ALLOWED_ATTRS_BY_TAG[tagName] ?? NO_ATTRS;
  return new Set([...GLOBAL_ATTRS, ...tagSpecific]);
}

function isSafeHref(href: string): boolean {
  const value = href.trim();
  if (!value) {
    return false;
  }

  const lower = value.toLowerCase();
  if (
    lower.startsWith('javascript:') ||
    lower.startsWith('data:') ||
    lower.startsWith('vbscript:')
  ) {
    return false;
  }

  if (value.startsWith('/')) {
    return !value.startsWith('//');
  }

  try {
    return SAFE_LINK_PROTOCOLS.has(new URL(value).protocol);
  } catch {
    return false;
  }
}

/** Keeps only attributes in the allowlist (compared case-insensitively). */
function keepOnlyAttributes(
  $el: CheerioSelection,
  allowedAttributes: Set<string>
): void {
  const attrs = $el.attr() ?? {};
  for (const name of Object.keys(attrs)) {
    if (!allowedAttributes.has(name.toLowerCase())) {
      $el.removeAttr(name);
    }
  }
}

function sanitizeInlineStyleAttribute($el: CheerioSelection): void {
  const inlineStyle = $el.attr('style');
  if (inlineStyle) {
    $el.attr('style', sanitizeCssText(inlineStyle));
  }
}

function sanitizeClassAttributeOnElement($el: CheerioSelection): void {
  const classAttr = $el.attr('class');
  if (!classAttr) {
    return;
  }
  const sanitized = sanitizeClassAttribute(classAttr);
  if (sanitized) {
    $el.attr('class', sanitized);
  } else {
    $el.removeAttr('class');
  }
}

function sanitizeAnchor($el: CheerioSelection): void {
  const href = $el.attr('href');
  keepOnlyAttributes($el, new Set([...ANCHOR_ATTRS, ...GLOBAL_ATTRS]));

  if (!href || !isSafeHref(href)) {
    $el.removeAttr('href');
    $el.removeAttr('rel');
    sanitizeClassAttributeOnElement($el);
    sanitizeInlineStyleAttribute($el);
    return;
  }

  $el.attr('href', href.trim());
  $el.attr('rel', 'noopener noreferrer');
  sanitizeClassAttributeOnElement($el);
  sanitizeInlineStyleAttribute($el);
}

function sanitizeAllowedElement($el: CheerioSelection, tagName: string): void {
  if (tagName === 'a') {
    sanitizeAnchor($el);
    return;
  }

  keepOnlyAttributes($el, getAllowedAttributes(tagName));
  sanitizeClassAttributeOnElement($el);
  sanitizeInlineStyleAttribute($el);
}

function sanitizeDom($dom: cheerio.CheerioAPI, root: CheerioSelection): void {
  // Deepest-first so unwrapping nested unsupported tags completes in one pass.
  const elements = root.find('*').toArray().reverse();

  for (const node of elements) {
    const tagName = getTagName(node);
    const $el = $dom(node);

    if (BLOCKED_TAGS.has(tagName)) {
      $el.remove();
      continue;
    }

    if (!ALLOWED_TAGS.has(tagName)) {
      $el.replaceWith($el.contents());
      continue;
    }

    sanitizeAllowedElement($el, tagName);
  }
}

export function sanitizeContactUsHtml(html: string | null | undefined): string {
  const content = html?.trim();
  if (!content) return '';

  const $dom = cheerio.load(`<div id="${SANITIZE_ROOT_ID}">${content}</div>`);
  const root = $dom(`#${SANITIZE_ROOT_ID}`);

  sanitizeDom($dom, root);

  return root.html()?.trim() ?? '';
}
